/**
 * Cross-tab session events.
 *
 * BroadcastChannel is the primary transport; a localStorage key mirrors every
 * message for contexts where it is unavailable. Both are used at once and the
 * receiver deduplicates by message id, so a tab never misses an event because
 * one transport is missing. Replaces the previous scheme, where `logout_event`
 * was listened for but never written by anyone.
 */

import { safeJsonParse } from './safeJson';

const CHANNEL_NAME = 'tempedge-session';
const MIRROR_KEY = 'tempedge:session:event';
const SEEN_LIMIT = 50;

export const SESSION_EVENTS = {
  TOKENS_UPDATED: 'TOKENS_UPDATED',
  SESSION_ENDED: 'SESSION_ENDED'
};

const listeners = new Set();
const seen = [];

let channel = null;

const alreadySeen = (id) => {
  if (!id || seen.includes(id)) return true;

  seen.push(id);
  if (seen.length > SEEN_LIMIT) seen.shift();
  return false;
};

const emit = (message) => {
  if (!message || alreadySeen(message.id)) return;

  listeners.forEach((listener) => listener(message));
};

const openChannel = () => {
  if (channel || typeof BroadcastChannel === 'undefined') return;

  try {
    channel = new BroadcastChannel(CHANNEL_NAME);
    channel.onmessage = (event) => emit(event.data);
  } catch (error) {
    channel = null;
  }
};

const handleStorage = (event) => {
  if (event.key !== MIRROR_KEY || !event.newValue) return;

  emit(safeJsonParse(event.newValue));
};

/** Sends an event to every other tab of this origin. The sender is not notified. */
export const publishSessionEvent = (type, payload = {}) => {
  openChannel();

  const message = { id: `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`, type, ...payload };
  seen.push(message.id);

  if (channel) {
    try {
      channel.postMessage(message);
    } catch (error) {
      channel = null;
    }
  }

  try {
    localStorage.setItem(MIRROR_KEY, JSON.stringify(message));
  } catch (error) {
    /* mirror is best-effort; BroadcastChannel already carried the message */
  }
};

/** Subscribes to cross-tab session events. Returns the unsubscribe function. */
export const onSessionEvent = (listener) => {
  openChannel();
  listeners.add(listener);
  return () => listeners.delete(listener);
};

if (typeof window !== 'undefined') {
  window.addEventListener('storage', handleStorage);
}
